/* eslint-disable camelcase */
import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';

import { MAXIMUM_ITEM_DESCRIPTION_LENGTH, MAX_ITEM_DESC_LENGTH } from '../../../utils/constants';
import { currencyFormatter, getPromoText } from '../../../utils/formatting';
import DiscountFormatter from '../DiscountFormatter';
import './style.css';

const { match, Default: _ } = require('@nike/rcf-fp');

const taxableFlag = (item) => match(item)(
  [() => (item?.Taxes?.map((tax) => tax.TaxSubType)[0] === 'Exempt'), ' E'],
  [() => item?.TaxableFlag, ' T'],
  [_, ' N']
);

const PERCENT_MULTIPLIER = 100;
const addGiftCardSaleDetails = (sale, RL_GiftCardNo, RL_GCBalance, RL_AuthCode, storeCurrency, storeLocale) => (
  <>
    <article className="justify-between">
      <p className="pl2-sm">{RL_GiftCardNo}: {sale.saleOfGiftCard.CardNumber?.replace(/.(?=.{4})/g, 'X')}</p>
    </article>
    <article className="justify-between">
      <p className="pl2-sm">{RL_GCBalance}: {currencyFormatter(storeLocale, storeCurrency, sale.saleOfGiftCard?.CurrentBalance?.Amount)}</p>
    </article>
    <article className="justify-between">
      <p className="pl2-sm">{RL_AuthCode}: {sale.saleOfGiftCard.Authorization?.AuthorizationCode}</p>
    </article>
  </>
);

const addPriceModifiers = (sale, RL_FinalPrice, RL_CouponNumber, storeCurrency, storeLocale, country) => (
  <>
    {sale.RetailPriceModifiers.map((modifier, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <section key={`${modifier?.Descriptions?.[0].Text}-${index}`}>
        {(modifier?.Amount?.Action === 'Subtract') && (
          <article className="justify-between">
            <p className="pl2-sm pr2-sm">{getPromoText(modifier).slice(0, MAX_ITEM_DESC_LENGTH)}
              {modifier?.Percent?.Percent ? ` ${(modifier?.Percent?.Percent * PERCENT_MULTIPLIER).toFixed()}%` : ''}
              {modifier?.VoidFlag && ` <Void>`}
            </p>
            <p>
              <DiscountFormatter isNegative storeLocale={storeLocale}>
                {currencyFormatter(
                  storeLocale,
                  storeCurrency,
                  modifier?.Amount?.Amount,
                )}
              </DiscountFormatter>
            </p>
          </article>
        )}
        {!modifier?.VoidFlag && modifier?.Reason && (
          <article className="justify-between">
            <p className="pl4-sm">{modifier?.Reason?.Description}</p><p />
          </article>
        )}
        {modifier?.Coupon && (
          <article className="justify-between">
            <p className="pl4-sm">{RL_CouponNumber} {modifier?.Coupon?.ID}</p><p />
          </article>
        )}
      </section>
    ))}
    <article className="justify-between">
      <p>{RL_FinalPrice}</p>
      <p>
        {currencyFormatter(
          storeLocale,
          storeCurrency,
          sale.ActualSalesUnitPrice?.Amount,
        )}
        {!['CHN', 'MAC', 'HKG', 'TWN', 'IND'].includes(country) && taxableFlag(sale)}
      </p>
    </article>
  </>
);

const PurchasedItems = ({
  saleForDelivery, saleForPickupItems, saleItems, country, sourcePOS
}) => {
  const {
    locale: { storeLocale, storeCurrency },
    RL_CouponNumber, RL_FinalPrice, RL_GiftCardNo, RL_GCBalance, RL_GiftItem, RL_AuthCode, RL_Size, RL_ShipStore, RL_TobeshippedNike, RL_TobeshippedNIKEId, RL_ITEMVOID, RL_Quantity
  } = useSelector((state) => state);
  const saleItemsWithOutShipping = saleItems.filter((item) => item.ItemType !== 'Shipping');
  const finalItems = [...saleForDelivery, ...saleForPickupItems, ...saleItemsWithOutShipping];
  // const size = (hierarchy) => hierarchy?.find((hierarchy1) => hierarchy1.Level === 'Size')?.Code;
  return (
    <>
      {finalItems.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <section key={`${item?.Descriptions?.[0].Text}-${index}`}>
          <article className="justify-between">
            <p>{item.voidFlag && (`<${RL_ITEMVOID}>`)}</p>
          </article>
          <article className="justify-between">
            <div>
              {['es-AR', 'th-TH'].includes(storeLocale) && `${item?.Quantity?.Quantity} `}
              {storeLocale === 'en-IN' ? (
                <>
                  <p style={{ textAlign: 'left' }}>
                    {item?.Descriptions?.find((i) => i.TypeCode === 'Short')?.Text
                      || item?.Descriptions?.find((i) => i.TypeCode === 'Long')?.Text
                      || 'UNNAMED ITEM'}
                  </p>
                </>
              ) : (
                item?.Descriptions?.find((i) => i.TypeCode === 'Short')?.Text.slice(0, MAXIMUM_ITEM_DESCRIPTION_LENGTH)
                || item?.Descriptions?.find((i) => i.TypeCode === 'Long')?.Text.slice(0, MAXIMUM_ITEM_DESCRIPTION_LENGTH)
                || 'UNNAMED ITEM'
              )}
            </div>
            <div>
              {storeLocale === 'en-IN' ? (
                <span style={{ whiteSpace: 'nowrap' }}>
                  MRP: {currencyFormatter(
                    storeLocale,
                    storeCurrency,
                    (item?.Quantity?.Quantity > 1 ? item?.ExtendedAmount?.Amount : item?.RegularSalesUnitPrice?.Amount),
                  )}
                </span>
              ) : (
                currencyFormatter(
                  storeLocale,
                  storeCurrency,
                  (item?.Quantity?.Quantity > 1 ? item?.ExtendedAmount?.Amount : item?.RegularSalesUnitPrice?.Amount),
                )
              )}
              {!['CHN', 'MAC', 'HKG', 'TWN', 'IND'].includes(country) && taxableFlag(item)}
            </div>
          </article>
          {item.saleOfGiftCard
            ? (addGiftCardSaleDetails(item, RL_GiftCardNo, RL_GCBalance, RL_AuthCode, storeCurrency, storeLocale))
            : (
              <>
                <article className="justify-between">
                  <p className="pl2-sm">
                    {item?.Item?.ID}&nbsp;&nbsp;
                    {sourcePOS === 'AssistInCloud'
                      ? item.MerchandiseHierarchy?.find((hierarchy1) => hierarchy1.Level === 'Size')?.Code
                      && `${RL_Size}: ${item.MerchandiseHierarchy?.find((hierarchy1) => hierarchy1.Level === 'Size')?.Code}`
                      : item?.NikeDigitalSizeDescription && `${RL_Size}: ${item?.NikeDigitalSizeDescription}`}
                    {['KOR', 'IND'].includes(country) && ` ${RL_Quantity}:${item?.Quantity?.Quantity || 1}`}
                  </p>
                </article>
                {item.GiftReceiptFlag && (
                <article className="justify-between">
                  <p className="pl2-sm">{RL_GiftItem}</p>
                </article>
                )}
              </>
            )}
          {storeLocale === 'en-IN' && (
            <>
              <article className="justify-between">
                {item?.Taxes?.[0]?.HSNNumber && <p className="pl2-sm">{`Rate: ${(item.Taxes.reduce((acc, tax) => acc + (tax.Percent || 0), 0) * 100)}%`} {`HSN: ${item.Taxes[0].HSNNumber}`}</p>}
              </article>
              <article className="justify-between">
                {item?.Quantity?.UniqueQuantityCode && <p className="pl2-sm">{`UQC: ${item.Quantity.UniqueQuantityCode}`}</p>}
              </article>
            </>
          )}
          {storeLocale === 'it-IT' && (
            <article className="justify-between">
              <p className="pl2-sm">{`Quantità: ${item?.Quantity?.Quantity}x`}</p>
              <p />
            </article>
          )}
          {item?.From && (
            <article className="justify-between">
              {item?.From?.LocationType === 'WAREHOUSE' && <p className="pl4-sm">{RL_TobeshippedNike}</p>}
              {item?.From?.LocationType === 'STORE' && <p className="pl4-sm">{RL_ShipStore}</p>}
              {item?.From?.LocationType === 'FACTORY' && <p className="pl4-sm">{RL_TobeshippedNIKEId}</p>}
            </article>
          )}
          {(item?.RetailPriceModifiers?.length > 0) && addPriceModifiers(item, RL_FinalPrice, RL_CouponNumber, storeCurrency, storeLocale, country)}
          {finalItems.length && <><br /><br /></>}
        </section>
      ))}
    </>
  );
};

PurchasedItems.defaultProps = {
  country: '',
  saleForDelivery: [],
  saleForPickupItems: [],
  saleItems: [],
  sourcePOS: ''
};

PurchasedItems.propTypes = {
  country: PropTypes.string,
  saleForDelivery: PropTypes.arrayOf(PropTypes.shape()),
  saleForPickupItems: PropTypes.arrayOf(PropTypes.shape()),
  saleItems: PropTypes.arrayOf(PropTypes.shape()),
  sourcePOS: PropTypes.string,
};

export default PurchasedItems;
